import { z } from 'zod';
import type { ContractError } from './errors';
import { toContractError } from './errors';
import type { Result } from './result';

const IsoDateTimeSchema = z.string().datetime({ offset: true });

const QueueManagerHealthCheckSchema = z.object({
  name: z.string(),
  ok: z.boolean(),
  latency_ms: z.number().nonnegative().nullish(),
  detail: z.string().nullish(),
});

const QueueManagerWorkerReadinessCheckSchema = z.object({
  name: z.string(),
  health_key: z.string(),
  ok: z.boolean(),
  last_seen_at: IsoDateTimeSchema.nullish(),
  age_seconds: z.number().nonnegative().nullish(),
  stale_after_seconds: z.number().int().nonnegative(),
  detail: z.string().nullish(),
});

export const QueueManagerReadyzResponseSchema = z.object({
  status: z.enum(['ready', 'not_ready']),
  service: z.string(),
  ts: IsoDateTimeSchema,
  checks: z.array(QueueManagerHealthCheckSchema).nullish(),
});

export const QueueManagerWorkerReadyzResponseSchema = z.object({
  status: z.enum(['ready', 'not_ready']),
  service: z.string(),
  ts: IsoDateTimeSchema,
  checks: z.array(QueueManagerWorkerReadinessCheckSchema),
});

export type QueueManagerReadyzResponse = z.infer<typeof QueueManagerReadyzResponseSchema>;

export type QueueManagerWorkerReadyzResponse = z.infer<typeof QueueManagerWorkerReadyzResponseSchema>;

const parseContract = <TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  raw: unknown,
): Result<z.infer<TSchema>, ContractError> => {
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      err: toContractError(parsed.error, { source: 'http' }),
    };
  }
  return { ok: true, value: parsed.data };
};

export const parseQueueManagerReadyzResponse = (raw: unknown) => parseContract(QueueManagerReadyzResponseSchema, raw);

export const parseQueueManagerWorkerReadyzResponse = (raw: unknown) =>
  parseContract(QueueManagerWorkerReadyzResponseSchema, raw);
